import type { Resource } from 'i18next'
import frFR from '../locales/fr_FR.json'
import enUS from '../locales/en_US.json'

/**
 * Single source of truth for supported locales (RV-60-H). Adding a language =
 * add its JSON import and one entry here; the i18n init and any language
 * selector derive everything from this map, so no other code changes.
 */
const sharedBundles = {
  fr_FR: frFR,
  en_US: enUS,
} as const

export type SupportedLocale = keyof typeof sharedBundles

export const SUPPORTED_LOCALES = Object.keys(sharedBundles) as SupportedLocale[]

/** Reference / source language and sole runtime fallback (V1 decision). */
export const DEFAULT_LOCALE: SupportedLocale = 'fr_FR'

// Per-feature locale files (D6 convention: features/{slug}/locales/{locale}.json),
// merged with the shared bundles below. Kept decoupled per feature (no cross-feature import).
const featureModules = import.meta.glob<Record<string, unknown>>(
  '../../features/*/locales/*.json',
  { eager: true, import: 'default' },
)

// Theme-owned locale files (themes/{slug}/locales/{locale}.json). Never put
// theme-specific copy in shared/locales - only the active structural theme is merged.
const themeModules = import.meta.glob<Record<string, unknown>>(
  '../../themes/*/locales/*.json',
  { eager: true, import: 'default' },
)

function mergeGlobTranslations(
  modules: Record<string, Record<string, unknown>>,
  locale: SupportedLocale,
  pathFilter?: (normalizedPath: string) => boolean,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {}
  for (const [path, translation] of Object.entries(modules)) {
    const normalized = path.replaceAll('\\', '/')
    if (!normalized.endsWith(`/${locale}.json`)) {
      continue
    }
    if (pathFilter !== undefined && !pathFilter(normalized)) {
      continue
    }
    Object.assign(merged, translation)
  }
  return merged
}

function mergeFeatureTranslations(locale: SupportedLocale): Record<string, unknown> {
  return mergeGlobTranslations(featureModules, locale)
}

function mergeActiveThemeTranslations(locale: SupportedLocale): Record<string, unknown> {
  const raw = import.meta.env.VITE_ACTIVE_THEME
  const activeSlug = typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : 'default'
  if (activeSlug === 'default') {
    return {}
  }

  return mergeGlobTranslations(
    themeModules,
    locale,
    (path) => path.includes(`/themes/${activeSlug}/locales/`),
  )
}

export const resources: Resource = Object.fromEntries(
  SUPPORTED_LOCALES.map((locale) => [
    locale,
    {
      translation: {
        ...mergeFeatureTranslations(locale),
        ...mergeActiveThemeTranslations(locale),
        ...sharedBundles[locale],
      },
    },
  ]),
)
